<template>
  <div
    v-if="user && user.artist"
    class="mobile-notification-controls"
  >
    <div class="mobile-controls-container">
      <followButton
        :user="user"
        :fullWidth="!artist.followed"
        :withIcon="true"
        :withUnfollow="true"
        class="mobile-follow-btn"
      />
      <notification-button
        v-if="artist.followed && artist.notifications"
        class="mobile-notification-btn"
        :class="{
          'on-bright-background': bodyBackgroundTextColor === '#000'
        }"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import followButton from '~/components/follow-button'
import notificationButton from '~/components/notification-button'

export default {
  name: 'MobileNotificationControls',
  components: {
    followButton,
    notificationButton
  },
  props: {
    user: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      dynamicScope: null,
      observer: null,
      bodyBackgroundTextColor: ''
    }
  },
  computed: {
    ...mapState(['artist'])
  },
  mounted() {
    this.dynamicScope = document.getElementById('dynamic-color-scope')

    // Create a MutationObserver to watch for changes in the element's style
    this.observer = new MutationObserver(() => {
      this.updateBodyBackgroundTextColor()
    })

    // Observe changes in the style attribute of the dynamicScope element
    if (this.dynamicScope) {
      this.observer.observe(this.dynamicScope, { attributes: true, attributeFilter: ['style'] })
    }
    this.updateBodyBackgroundTextColor()
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
    }
  },
  methods: {
    updateBodyBackgroundTextColor() {
      if (this.dynamicScope) {
        this.bodyBackgroundTextColor = getComputedStyle(this.dynamicScope).getPropertyValue('--body-background-text-color')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "assets/sass/_variables";

.mobile-notification-controls {
  padding: 15px 20px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  margin: 0 auto 20px auto;
  max-width: 492px;
  width: calc(100% - 30px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  .mobile-controls-container {
    display: flex;
    gap: 12px;
    align-items: center;
    justify-content: center;

    .mobile-follow-btn {
      flex: 1;
      border-radius: 8px;
      font-weight: 600;
      padding: 12px 16px;
      transition: all 0.2s ease;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }

      :deep(i) {
        font-size: 0.9rem;
      }

      :deep(span) {
        padding-left: 6px;
        font-size: 0.95rem;
      }
    }

    .mobile-notification-btn {
      display: flex !important;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      border-radius: 8px;
      padding: 12px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      transition: all 0.2s ease;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

      &:hover {
        transform: translateY(-1px);
        background: rgba(255, 255, 255, 0.15);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }

      :deep(i) {
        font-size: 1.8rem;
        color: var(--primary-color, #007bff);
      }

      &.on-bright-background {
        background: rgba(0, 0, 0, 0.05);
        border-color: rgba(0, 0, 0, 0.1);

        &:hover {
          background: rgba(0, 0, 0, 0.1);
        }
      }
    }
  }
}

// Dark mode adjustments
@media (prefers-color-scheme: dark) {
  .mobile-notification-controls {
    background: rgba(0, 0, 0, 0.3);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);

    .mobile-controls-container {
      .mobile-notification-btn {
        background: rgba(255, 255, 255, 0.08);
        border-color: rgba(255, 255, 255, 0.15);

        &:hover {
          background: rgba(255, 255, 255, 0.12);
        }
      }
    }
  }
}
</style>
